/**
 * 🧑‍🏫 **선생님 화면** — 우리 반 스무 명이 지금 어디까지 했는지 한 장에.
 *
 * ## 왜 필요한가
 * 방과후 교실에서 선생님은 태블릿 스무 대를 등 뒤로 돌아다니며 본다. 누가 막혔는지,
 * 누가 다 했는지 **화면을 하나하나 들여다봐야** 안다. 그 시간이 수업의 절반을 먹는다.
 * 원격 수업이면 아예 못 본다.
 *
 * ## 🔴 손 든 아이가 맨 위
 * 「도와주세요」를 누른 아이가 목록 아래쪽에 묻히면 이 화면은 없는 것과 같다.
 * 정렬은 이름순도 진도순도 아니고 **손 든 순서**가 먼저다.
 *
 * ## 🔴 「잘했어요」는 선생님만
 * 회원가입이 없으니 로그인으로 막을 수 없다. 대신 **반을 처음 연 사람이 선생님 열쇠를 받는다** —
 * 반 코드를 만들어 칠판에 적는 사람이 선생님이기 때문이다. 열쇠 없이는 칭찬을 못 보낸다.
 * (열쇠를 아이에게 알려 주면 아이도 보낼 수 있다. 그건 막을 방법이 없고, 막을 일도 아니다.)
 *
 * ## 🔴 실시간 서버를 쓰지 않는다(불변식 3)
 * 한 반은 많아야 서른 명이고 수업은 한 시간이다. 4초에 한 번 물어보면 충분하다.
 * 이걸 위해 VM 을 상시 켜 두면, 수업이 없는 날에도 서버비가 나간다.
 */
import { HERO_CODE_ALPHABET } from "./hero";
import type { SavedHero } from "./index";

/* ── 선생님 열쇠 ──────────────────────────────────────── */

export const TEACHER_KEY_LENGTH = 4;

/** 반 코드 뒤에 붙는 네 글자. 헷갈리는 글자(I·L·O·U·0·1)는 히어로 코드와 같은 이유로 뺀다. */
export function generateTeacherKey(rand: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < TEACHER_KEY_LENGTH; i++) {
    // % 는 rand() 가 1 을 줬을 때 배열 밖으로 나가는 것을 막는다(히어로 코드와 같은 방식)
    out += HERO_CODE_ALPHABET[Math.floor(rand() * HERO_CODE_ALPHABET.length) % HERO_CODE_ALPHABET.length];
  }
  return out;
}

export function isTeacherKey(v: unknown): v is string {
  if (typeof v !== "string" || v.length !== TEACHER_KEY_LENGTH) return false;
  for (const ch of v.toUpperCase()) if (!HERO_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

/** 선생님이 적어 둘 열쇠 — 반 코드와 함께 한 줄로 보여 준다. */
export function teacherLabel(classCode: string, key: string): string {
  return `${classCode} · 선생님 열쇠 ${key}`;
}

/* ── 신호 ────────────────────────────────────────────── */

/** 주고받는 신호. 이 둘 말고는 없다 — 늘어나면 여기부터 고친다. */
export const CLASS_SIGNALS = ["hand", "praise"] as const;
export type ClassSignal = (typeof CLASS_SIGNALS)[number];

export function isClassSignal(v: unknown): v is ClassSignal {
  return typeof v === "string" && (CLASS_SIGNALS as readonly string[]).includes(v);
}

/**
 * 신호가 살아 있는 시간 — 한 교시(50분).
 *
 * 🔴 **남아 있으면 안 된다.** 지난주에 든 손이 이번 주 화면에 떠 있으면 선생님은
 * 유령을 쫓아다닌다. 아이가 내리는 것을 잊어도 시간이 지나면 저절로 내려간다.
 */
export const SIGNAL_TTL_MS = 50 * 60 * 1000;

export function signalAlive(at: string, now: number): boolean {
  const t = Date.parse(at);
  return Number.isFinite(t) && now - t < SIGNAL_TTL_MS;
}

/**
 * 한 화면에 보여 주는 아이 수 상한.
 *
 * 🔴 **세 곳이 따로 60을 알고 있었다** — 선생님 명단(`class_member`), 선생님이 세는 진도
 * (`story`), 전시장(`story`). 게다가 진도 쪽만 **정렬이 없어서** 61명째부터는
 * 「명단엔 있는데 글은 안 뽑힌」 아이가 생겼다. 선생님 화면엔 `아직 시작 전` 으로 뜬다 —
 * 아이는 옆에서 다 썼는데. 아무 에러도 안 난다.
 *
 * 반 명단은 **저절로 줄지 않는다**(정리 기본값이 「안 지움」 — 아이 글을 함부로 지우지
 * 않기로 했다). 그래서 같은 반 코드를 학기마다 다시 쓰면 인원이 쌓여 이 선을 넘는다.
 *
 * 이제 세 곳이 이 값을 쓰고, **잘렸으면 화면이 말한다**(`classCutSay`).
 */
export const CLASS_LIST_MAX = 60;

/**
 * 잘렸을 때 화면에 띄우는 말. 「왜 안 보이지」를 겪게 두지 않는다.
 *
 * 🔴 숫자만 주면 선생님은 못 고친다 — **무엇을 하면 되는지**까지 적는다.
 */
export function classCutSay(shown: number): string {
  return `한 번에 ${shown}명까지 보여요. 더 있는 반은 최근에 온 친구부터 나와요 — 반을 나누면 다 보여요.`;
}

/* ── 반 한 장 ────────────────────────────────────────── */

export interface ClassMember {
  heroCode: string;
  /** 아이가 지은 별명. 없으면 코드로 부른다 — 실명은 어디에도 없다(불변식 1). */
  nickname?: string;
  /**
   * 아이가 만든 캐릭터의 모양. 전시장·선생님 화면이 이걸로 그린다.
   *
   * 🔴 히어로 표를 조회하지 않는다 — 캐릭터는 이 기기에만 있어도 되고(M1),
   * 그런 아이는 서버에 없어서 전시장에서 혼자 빈 칸이 된다.
   */
  hero?: SavedHero;
  /** 몇 쪽 썼나(0~6). */
  filled: number;
  /** 손을 든 시각. 안 들었으면 없음. */
  handAt?: string;
  /** 칭찬받은 시각. */
  praiseAt?: string;
}

/** 화면에 부를 이름. 별명이 없으면 코드 — 「이름 없음」이라고 쓰면 누구인지 못 찾는다. */
export function memberLabel(m: ClassMember): string {
  const n = m.nickname?.trim();
  return n && n.length > 0 ? n : m.heroCode;
}

/**
 * 선생님이 보는 순서.
 *
 * ① 손 든 아이 — 먼저 든 순서대로(기다린 만큼 먼저)
 * ② 나머지는 **덜 쓴 아이가 위로** — 막힌 아이를 먼저 보라는 뜻이다.
 *    다 쓴 아이를 위에 두면 화면이 「잘하고 있다」만 보여 주고, 그건 도움이 안 된다.
 * ③ 같으면 코드순 — 새로고침할 때마다 순서가 흔들리면 선생님이 눈으로 못 쫓는다
 */
export function sortClassBoard(members: readonly ClassMember[]): ClassMember[] {
  return [...members].sort((a, b) => {
    if (!!a.handAt !== !!b.handAt) return a.handAt ? -1 : 1;
    if (a.handAt && b.handAt && a.handAt !== b.handAt) return a.handAt < b.handAt ? -1 : 1;
    if (a.filled !== b.filled) return a.filled - b.filled;
    return a.heroCode < b.heroCode ? -1 : 1;
  });
}

/** 아이 하나의 상태 한 줄 — 선생님이 훑는 글이라 숫자보다 말이 빠르다. */
export function progressSay(filled: number, total: number): string {
  if (filled === 0) return "아직 시작 전";
  if (filled >= total) return "다 썼어요";
  return `${filled} / ${total} 쪽`;
}

/** 반 전체 한 줄 — 화면 맨 위에 둔다. 「누가 손 들었나」가 제일 급하다. */
export function boardSummary(members: readonly ClassMember[], total: number): string {
  if (members.length === 0) return "아직 들어온 친구가 없어요";
  const hands = members.filter((m) => m.handAt).length;
  const done = members.filter((m) => m.filled >= total).length;
  const parts = [`${members.length}명`];
  if (hands > 0) parts.push(`✋ ${hands}명이 기다려요`);
  parts.push(`다 쓴 친구 ${done}명`);
  return parts.join(" · ");
}

/* ── 🧑‍🏫 수업 여는 법 ───────────────────────────────── */

/**
 * 선생님이 처음 열 때 밟는 순서.
 *
 * 🔴 **어디에도 안 적혀 있었다.** 기능은 다 만들어 두고 「어떻게 시작하나요」를 안 적으면
 * 선생님은 첫 수업에서 화면만 보다가 끝낸다. 방과후 강사는 이 서비스를 처음 본다 —
 * 반 코드가 뭔지, 열쇠를 왜 적어야 하는지 아무도 안 알려 준다.
 *
 * 판정을 여기 두는 이유: 선생님 화면과 칠판 안내문이 **같은 말**을 해야 한다.
 * 두 곳에 따로 쓰면 한쪽만 고쳐지고, 종이와 화면이 다른 순서를 알려 준다.
 */
export interface ClassStep {
  /** 화면에 보일 번호. */
  n: number;
  say: string;
  /** 왜 그래야 하는지. 이유를 빼면 「그냥 시키니까」가 되고 그러면 안 지킨다. */
  why?: string;
}

export const TEACHER_STEPS: readonly ClassStep[] = [
  { n: 1, say: "「＋ 새 반 만들기」를 누르세요.", why: "반 코드와 선생님 열쇠가 한 번에 나와요." },
  {
    n: 2,
    say: "선생님 열쇠 네 글자를 적어 두세요.",
    why: "다시 볼 수 없어요. 적어 두면 다른 기기에서도, 다음 주에도 도로 넣을 수 있어요.",
  },
  {
    n: 3,
    say: "반 코드를 칠판에 적거나 안내문을 뽑아 붙이세요.",
    why: "아이들은 이 코드로 들어와요. 열쇠는 적지 마세요.",
  },
  { n: 4, say: "아이들이 자기 친구(캐릭터)를 만들어요.", why: "여섯 글자 코드가 아이마다 생겨요." },
  {
    n: 5,
    say: "「📖 내 그림책」에서 한 쪽씩 써요.",
    why: "쓰면 저절로 저장돼요. 누를 필요 없어요.",
  },
  {
    n: 6,
    say: "이 화면에서 진도를 보고 ✋ 든 친구를 도와주세요.",
    why: "손 든 친구가 늘 맨 위에 있어요.",
  },
  /**
   * 🔴 **있는 줄 모르면 없는 기능이다.** 전시장에 걸리면 안 될 글이 나왔을 때
   * 선생님이 이 단추를 모르면, 반을 통째로 지우거나 인쇄를 포기한다.
   */
  {
    n: 7,
    say: "전시장에 걸기 곤란한 글은 「🙈 전시장에서 빼기」를 누르세요.",
    why: "전시장과 인쇄에서만 빠져요. 아이와 부모 화면에는 그대로 있고, 언제든 다시 걸 수 있어요.",
  },
];

/** 아이가 칠판 안내문에서 읽을 순서. 글자를 갓 뗀 아이도 읽을 만큼 짧게. */
export const CHILD_STEPS: readonly ClassStep[] = [
  { n: 1, say: "QR 을 찍거나 주소를 열어요" },
  { n: 2, say: "반 코드를 넣어요" },
  { n: 3, say: "내 친구를 만들고 이야기를 써요" },
];

/* ── 🏫 운영자가 보는 반 목록 ─────────────────────────── */

/**
 * 반 하나를 한 줄로. **출판사가 방과후 수업을 팔고 지원하려면 이게 있어야 한다** —
 * 반이 몇 개인지, 아이가 몇 명인지, 그림책이 몇 권 나왔는지 모르면
 * 「잘 되고 있나요」에 답할 수가 없다.
 *
 * 🔴 여기에도 아이 정보는 없다(불변식 1). 반 코드와 **숫자**뿐이다 —
 * 누가 썼는지는 이 줄만 봐서는 알 수 없고, 알 필요도 없다.
 */
export interface ClassRoomRow {
  classCode: string;
  /** 반을 연 날. 선생님 화면에서 만들지 않고 코드만 나눠 쓴 반은 없을 수 있다. */
  createdAt?: string;
  /** 들어온 아이 수. */
  members: number;
  /** 그림책을 한 쪽이라도 쓴 아이 수. */
  writing: number;
  /** 여섯 쪽을 다 쓴 아이 수. */
  done: number;
  /** 마지막으로 무언가 일어난 시각. */
  lastAt?: string;
  /**
   * 🎥 **선생님이 우리 반 화상 링크를 넣어 뒀나.**
   *
   * 주소 자체는 안 준다 — 운영자가 알 필요가 없고, 목록에 회의 주소가 줄줄이 뜨면
   * 그 자체가 흘릴 거리가 된다. **넣었나 안 넣었나**만 있으면 지원할 수 있다:
   * 안 넣은 반은 이름만 맞히면 누구나 들어오는 자동 방을 쓰고 있다는 뜻이라,
   * 「비밀번호 걸 수 있는 방을 넣으시라」고 안내할 자리가 된다.
   */
  ownMeet?: boolean;
}

/**
 * 운영자가 보는 순서 — **최근에 움직인 반이 위**.
 *
 * 이름순이면 지난 학기 반이 맨 위에 눌러앉는다. 운영자가 이 화면을 여는 이유는
 * 대부분 「지금 돌아가는 수업」을 보려는 것이다.
 */
export function sortClassRooms(rows: readonly ClassRoomRow[]): ClassRoomRow[] {
  return [...rows].sort((a, b) => {
    const at = a.lastAt ?? a.createdAt ?? "";
    const bt = b.lastAt ?? b.createdAt ?? "";
    if (at !== bt) return at < bt ? 1 : -1;
    return a.classCode < b.classCode ? -1 : 1;
  });
}

/** 반 하나를 한 줄 말로 — 숫자만 늘어놓으면 운영자가 뜻을 못 읽는다. */
export function classRoomSay(r: ClassRoomRow): string {
  if (r.members === 0 && r.writing === 0) return "아직 아무도 안 들어왔어요";
  const parts = [`${Math.max(r.members, r.writing)}명`];
  if (r.writing > 0) parts.push(`쓰는 중 ${r.writing}명`);
  if (r.done > 0) parts.push(`다 쓴 그림책 ${r.done}권`);
  return parts.join(" · ");
}

/** 전체 요약 — 화면 맨 위 한 줄. */
export function classesSummary(rows: readonly ClassRoomRow[]): string {
  if (rows.length === 0) return "아직 열린 반이 없어요";
  const kids = rows.reduce((s, r) => s + Math.max(r.members, r.writing), 0);
  const books = rows.reduce((s, r) => s + r.done, 0);
  return `반 ${rows.length}개 · 아이 ${kids}명 · 완성한 그림책 ${books}권`;
}

/**
 * 🎥 **이 반이 선생님이 정한 방을 쓰나** — 목록에 붙일 작은 표시.
 *
 * 🔴 **안 쓰는 반에는 아무것도 안 붙인다.** 대부분이 자동 방이라, 거기에 경고를 달면
 * 목록이 통째로 빨개지고 곧 아무도 안 본다(이 저장소가 여러 번 겪은 것이다).
 * 대신 **된 것만** 표시하고, 전체 그림은 아래 한 줄로 준다.
 *
 * 모르면(옛 서버라 칸이 없음) 아무 말도 안 한다 — 「없다」와 「못 물어봤다」는 다르다.
 */
export function classMeetChip(r: ClassRoomRow): string | null {
  return r.ownMeet === true ? "🎥 선생님 방" : null;
}

/**
 * 🎥 **몇 반이 제 방을 쓰나** — 운영자가 한눈에 보고 나머지를 챙길 수 있게.
 *
 * 자동 방은 **이름만 맞히면 누구나 들어온다.** 그 반이 몇 개인지가 지원할 거리다.
 * 아무 반도 그 칸을 모르면(옛 서버) `null` — 없는 숫자를 지어내지 않는다.
 */
export function classMeetSummary(rows: readonly ClassRoomRow[]): string | null {
  const known = rows.filter((r) => r.ownMeet !== undefined);
  if (known.length === 0) return null;
  const own = known.filter((r) => r.ownMeet).length;
  if (own === known.length) return `🎥 모든 반이 선생님이 정한 방을 써요`;
  return `🎥 선생님이 정한 방 ${own}/${known.length}반 — 나머지는 이름만 알면 누구나 들어와요`;
}
